import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { ContextItemType } from 'src/@types/ai';
import {
  type PopoverProps,
  PopoverTrigger,
} from 'src/components/PopoverTrigger';
import { ExplorerMenu } from 'src/components/explorer/shared/ExplorerMenu';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { AIContextSearch } from 'src/modules/ai-conversation/components/AIInput/AIActionButtons/AIContextSearch';
import { AIContextDocsMenu } from 'src/modules/ai-conversation/components/AIInput/AIContextSelectPopover/AIContextDocsMenu';
import { AIContextDriveMenu } from 'src/modules/ai-conversation/components/AIInput/AIContextSelectPopover/AIContextDriveMenu';
import { AIContextSelectionList } from 'src/modules/ai-conversation/components/AIInput/AIContextSelectPopover/AIContextSelectionList';
import { AIContextUpload } from 'src/modules/ai-conversation/components/AIInput/AIContextSelectPopover/AIContextUpload';
import { AiContextRecentLists } from 'src/modules/ai-conversation/components/AIInput/AIContextSelectPopover/AiContextRecentLists';
import { AIInputStateControl } from 'src/modules/ai-conversation/components/AIInput/useAIInputState';
type Props = PopoverProps<{
  control: AIInputStateControl;
}>;

export const AIContextSelectPopover = ({
  control,
  target,
  handleClose,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { translate } = useLocales();
  const { contextFiles, toggleContextFiles, hasContextFile, currentDocument } =
    control();

  const contextTitle = useMemo(() => {
    if (searchTerm.length) return translate('search.labels.search_results');
    if (contextFiles.length) return translate('search.labels.added');
    return translate('header.nav.recents');
  }, [contextFiles.length, searchTerm.length, translate]);

  return (
    <ExplorerMenu
      open
      anchorEl={target}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            height: 450,
            width: 1,
            '& .MuiList-root': {
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 450,
              flexGrow: 1,
              overflow: 'hidden',
            },
          },
        },
      }}
    >
      <StyledMenuItemInput onKeyDown={(e) => e.stopPropagation()}>
        <StyledInput
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={translate(
            'search.placeholders.search_files_folders_docs',
          )}
        />
      </StyledMenuItemInput>

      <Typography variant="caption" color="text.secondary">
        {contextTitle}
      </Typography>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
        }}
      >
        {searchTerm ? (
          <AIContextSearch
            searchTerm={searchTerm}
            selectDoc={(doc) => toggleContextFiles(ContextItemType.DOC, doc)}
            selectFile={(drive) =>
              toggleContextFiles(ContextItemType.DRIVE, drive)
            }
            hasContextFile={hasContextFile}
          />
        ) : (
          <>
            {!!contextFiles.length ? (
              <AIContextSelectionList
                contextFiles={contextFiles}
                handleToggleFile={toggleContextFiles}
              />
            ) : (
              <AiContextRecentLists control={control} />
            )}
          </>
        )}
      </Box>

      <Divider component="li" sx={{ my: 1 }} />

      {currentDocument && (
        <MenuItem
          onClick={() =>
            toggleContextFiles(ContextItemType.DOC, currentDocument)
          }
        >
          <ListItemIcon>
            <Iconify icon="tabler:file-spark" color="text.secondary" />
          </ListItemIcon>
          <ListItemText>
            {translate('shared.labels.current_document')}
          </ListItemText>
          {hasContextFile(ContextItemType.DOC, currentDocument) && (
            <Iconify icon="tabler:check" color="success.main" />
          )}
        </MenuItem>
      )}

      <AIContextUpload control={control} />

      <PopoverTrigger popover={AIContextDocsMenu} context={{ control }}>
        <MenuItem>
          <ListItemIcon>
            <Iconify icon="tabler:file" color="text.secondary" />
          </ListItemIcon>
          <ListItemText>
            {translate('header.nav_vertical.board_tab.docs')}
          </ListItemText>
          <Iconify icon="tabler:chevron-right" color="text.secondary" />
        </MenuItem>
      </PopoverTrigger>

      <PopoverTrigger popover={AIContextDriveMenu} context={{ control }}>
        <MenuItem>
          <ListItemIcon>
            <Iconify icon="tabler:folder" color="text.secondary" />
          </ListItemIcon>
          <ListItemText>{translate('header.nav_vertical.drive')}</ListItemText>
          <Iconify icon="tabler:chevron-right" color="text.secondary" />
        </MenuItem>
      </PopoverTrigger>
    </ExplorerMenu>
  );
};

const StyledMenuItemInput = styled(MenuItem)(({ theme }) => ({
  '&.MuiMenuItem-root': {
    padding: '0 !important',
  },
  '&:hover, &.Mui-focusVisible': {
    background: 'none',
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
}));

const StyledInput = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: `${theme.spacing(0, 0, 1)} !important`,
    ...theme.typography.body2,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
